import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { 
  ArrowLeft, 
  Award,
  Calendar,
  Mail,
  CheckCircle,
  Clock,
  AlertCircle,
  Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { QHubBuddyLogo, QHubFooter } from "@/components/QHubBranding";

const Certifications = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchCertifications = async () => {
      try {
        const params = filter !== "all" ? { status: filter } : {};
        const response = await axios.get(`${API}/certificaciones`, {
          params,
          withCredentials: true
        });
        setCertifications(response.data);
      } catch (error) {
        console.error("Error fetching certifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCertifications();
  }, [filter]);

  const getStatusConfig = (status) => {
    switch (status) {
      case "sent":
        return { icon: CheckCircle, color: "text-green-600", bgColor: "bg-green-50", label: "Enviado" };
      case "pending":
        return { icon: Clock, color: "text-yellow-600", bgColor: "bg-yellow-50", label: "Pendiente" };
      case "failed":
        return { icon: AlertCircle, color: "text-red-600", bgColor: "bg-red-50", label: "Error" };
      default:
        return { icon: Clock, color: "text-gray-600", bgColor: "bg-gray-50", label: status };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-SV", { day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-mesh" data-testid="certifications-page">
      <header className="qhub-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900" onClick={() => navigate("/dashboard")} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <QHubBuddyLogo size="small" />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[160px]" data-testid="filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="sent">Enviados</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-bold font-['Outfit'] text-gray-900">Mis Certificaciones</h1>
          <p className="text-gray-500">Historial de logros y certificados obtenidos</p>
        </div>
      </div>

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#419cca] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : certifications.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
              <Award className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No tienes certificaciones aún</h3>
            <p className="text-gray-500 mb-6">Completa tus sesiones de aprendizaje para obtener certificados</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white rounded-xl px-8">
              Ir a estudiar
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {certifications.map((cert, index) => {
              const statusConfig = getStatusConfig(cert.status);
              const StatusIcon = statusConfig.icon;
              return (
                <motion.div key={cert.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`certification-card-${cert.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#419cca]/10 to-[#8b5cf6]/10 flex items-center justify-center shrink-0">
                      <Award className="w-7 h-7 text-[#419cca]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{cert.topic}</h3>
                        <Badge className={`${statusConfig.bgColor} ${statusConfig.color} border-0 shrink-0`}>
                          <StatusIcon className="w-3 h-3 mr-1" />{statusConfig.label}
                        </Badge>
                      </div>
                      <p className="text-gray-500 mb-3 line-clamp-2">{cert.summary}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(cert.created_at)}</span>
                        <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{cert.teacher_email}</span>
                      </div>
                      {cert.ods && <div className="mt-3 flex flex-wrap gap-2">{cert.ods.split(",").map((ods, i) => <span key={i} className="ods-badge">{ods.trim()}</span>)}</div>}
                      {cert.reflection && <div className="mt-4 p-4 bg-gray-50 rounded-xl"><p className="text-sm text-gray-500 italic">"{cert.reflection}"</p></div>}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
      <QHubFooter />
    </div>
  );
};

export default Certifications;
